// Local Territory Rush: solo vs computer, two players on one device, and the tutorial sandbox.
import { TerritoryGame, TICK_MS, mapSizeFor, rng, type Dir } from '../../shared/territory/engine';
import { botThink, newBotMemory, type BotLevel, type BotMemory } from '../../shared/territory/ai';
import { FlowGame, STEP_MS } from '../../shared/territory/flow';
import { flowBotThink, newFlowBotMemory, type FlowBotMemory } from '../../shared/territory/flow-ai';
import { TerritoryClient } from './client';
import { assignColors } from '../../shared/territory/colors';

export interface LocalTerritoryOptions {
  /** color: requested palette index (TERRITORY_COLORS); bots take the remaining colours, never a duplicate */
  humans: { name: string; color?: number }[];
  bots: number;
  level: BotLevel;
  durationMin: number;
  seed?: number;
  /** custom map size (tutorial) */
  size?: number;
  countdownMs?: number;
  /** continuous steering (T2): point/touch at any angle; bots steer the same way */
  flow?: boolean;
}

export class LocalTerritory extends TerritoryClient {
  readonly opts: LocalTerritoryOptions;
  private bots: { idx: number; mem: BotMemory; fmem: FlowBotMemory; rand: () => number; level: BotLevel }[] = [];
  private paused = false;
  private hiddenAt = 0;
  /** hook used by the tutorial to script the board before each step */
  beforeStep: ((g: TerritoryGame) => void) | null = null;
  onStep: ((g: TerritoryGame) => void) | null = null;
  /** Hidden page = paused, in the countdown and in play; on return the countdown keeps its time left and play
   *  resumes from now (no catch-up steps). */
  private onVis = () => {
    const now = performance.now();
    if (document.hidden) {
      if (!this.paused) this.hiddenAt = now;
      this.paused = true;
      return;
    }
    if (!this.paused) return;
    this.paused = false;
    if (this.status === 'countdown') this.countdownEnd += now - this.hiddenAt;
    this.lastTickAt = now;
  };

  constructor(opts: LocalTerritoryOptions) {
    super();
    this.opts = opts;
    this.restart();
    document.addEventListener('visibilitychange', this.onVis);
  }

  restart() {
    const o = this.opts;
    const n = o.humans.length + o.bots;
    const size = o.size ?? mapSizeFor(n);
    const seed = o.seed ?? Math.floor(Math.random() * 2 ** 31);
    this.tickMs = o.flow ? STEP_MS : TICK_MS;
    const cfg = { width: size, height: size, durationTicks: Math.round((o.durationMin * 60000) / this.tickMs), seed };
    const g = o.flow ? new FlowGame(cfg) : new TerritoryGame(cfg);
    const colors = assignColors([...o.humans.map((h) => h.color), ...Array.from({ length: o.bots }, () => null)]);
    o.humans.forEach((h, k) => g.addPlayer(`h${k}`, h.name, false, colors[k]));
    const r = rng(seed ^ 0xabcdef);
    this.bots = [];
    for (let k = 0; k < o.bots; k++) {
      const p = g.addPlayer(`b${k}`, botName(k), true, colors[o.humans.length + k]);
      this.bots.push({ idx: p.idx, mem: newBotMemory(), fmem: newFlowBotMemory(), rand: rng(Math.floor(r() * 2 ** 31)), level: o.level });
    }
    g.start();
    this.game = g;
    this.me = o.humans.map((_, k) => k);
    this.end = null;
    this.feed = [];
    this.resetBuffers(g);
    this.status = 'countdown';
    this.countdownEnd = performance.now() + (o.countdownMs ?? 2500);
    this.emit();
  }

  steer(slot: number, d: Dir) {
    const idx = this.me[slot];
    if (idx === undefined || !this.game || this.status === 'ended') return;
    if (this.flow && this.status !== 'playing') return; // continuous: the countdown never takes an input
    this.game.steer(idx, d);
  }

  steerAngle(slot: number, angle: number) {
    const idx = this.me[slot];
    const g = this.flow;
    if (idx === undefined || !g || this.status !== 'playing') return;
    g.setTarget(idx, angle);
  }

  update(now: number) {
    const g = this.game;
    if (!g || this.paused) return;
    if (this.status === 'countdown') {
      if (now >= this.countdownEnd) {
        this.status = 'playing';
        this.lastTickAt = now;
        this.emit();
      }
      return;
    }
    if (this.status !== 'playing') return;
    let steps = 0;
    while (now - this.lastTickAt >= this.tickMs && steps < 5) {
      this.stepOnce(this.lastTickAt + this.tickMs);
      steps++;
      if (this.status !== 'playing') break;
    }
    if (steps === 5) this.lastTickAt = now; // fell far behind (tab throttled): don't fast-forward
  }

  private stepOnce(at: number) {
    const g = this.game!;
    const fg = this.flow;
    for (const b of this.bots) {
      if (fg) flowBotThink(fg, b.idx, b.fmem, b.level, b.rand);
      else botThink(g, b.idx, b.mem, b.level, b.rand);
    }
    this.beforeStep?.(g);
    this.beforeTick(g);
    g.step();
    this.afterTick(
      at,
      g.ownerChanges.map((c) => c.i),
      g.ownerChanges.map((c) => c.v),
      g.events,
    );
    this.onStep?.(g);
    const humansAlive = this.me.filter((i) => g.players[i].alive);
    if (g.result) {
      this.status = 'ended';
      this.end = { result: g.result };
    } else if (this.me.length === 1 && humansAlive.length === 0) {
      // solo: the game ends for you when you are knocked out
      const alive = g.players.filter((p) => p.alive).sort((a, b) => b.land - a.land);
      const dead = g.players.filter((p) => !p.alive).sort((a, b) => (b.diedAt ?? 0) - (a.diedAt ?? 0));
      this.status = 'ended';
      this.end = { result: { winner: null, reason: 'last_standing', ranking: [...alive, ...dead].map((p) => p.idx) } };
    }
    this.emit();
  }

  dispose() {
    document.removeEventListener('visibilitychange', this.onVis);
  }
}

const BOT_NAMES = ['Nova', 'Pixel', 'Bolt', 'Mochi', 'Echo', 'Kiwi', 'Comet', 'Tofu'];
export function botName(k: number) {
  return BOT_NAMES[k % BOT_NAMES.length];
}
